import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Visibility as ViewIcon,
  ExitToApp as DischargeIcon,
} from '@mui/icons-material';

const BedCard = styled(Card)(({ theme, status }) => ({
  height: '90px', // Reduced from 120px to make boxes shorter
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  background: status === 'occupied' ? '#fff5f5' : '#f0fff4',
  borderLeft: `4px solid ${status === 'occupied' ? '#f56565' : '#48bb78'}`,
  borderRadius: '8px',
  '&:hover': {
    transform: 'translateY(-2px)', // Reduced from -4px
    boxShadow: theme.shadows[3], // Reduced shadow
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(1.5), // Reduced padding
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  '& .MuiIconButton-root': {
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
}));

const BedAssignmentGrid = ({ 
  departments = [],
  availableBeds = [],
  availableRooms = [],
  activeBedAssignments = [],
  activeRoomAssignments = [],
  onRefresh,
  onAssign,
  onDischarge,
  onView,
  searchTerm = '',
  loading = false,
  selectedDepartment = 'all'
}) => {

  const calculateDaysOccupied = (startDate) => {
    const start = new Date(startDate);
    const now = new Date();
    const diffTime = Math.abs(now - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Helper function to extract room name from bed data
  const getRoomName = (bed) => {
    console.log('roomId', bed.roomId);
    // For beds, roomId contains the actual room identifier (e.g., "ED-R1", "PAIN-R1")
    if (bed.roomId && bed.roomId !== 'Unknown' && bed.roomId !== 'Unknown Room') {
      return bed.roomId;
    }
    
 
    // If bed name contains room info (e.g., "ED-R1-B1"), extract room part
    if (bed.name && bed.name.includes('-')) {
      const parts = bed.name.split('-');
      if (parts.length >= 2) {
        return `${parts[0]}-${parts[1]}`; // e.g., "ED-R1" from "ED-R1-B1"
      }
    }
    
    // If bed ID contains room info
    if (bed.bedId && bed.bedId.includes('-')) {
      const parts = bed.bedId.split('-');
      if (parts.length >= 2) {
        return `${parts[0]}-${parts[1]}`;
      }
    }
    
    // Try to extract from department name if it's meaningful
    if (bed.departmentName && bed.departmentName !== 'Unknown' && bed.departmentName !== 'Unknown Department') {
      return bed.departmentName;
    }
    
    // If we have a name that looks like a department code, use it
    if (bed.name && (bed.name.startsWith('ED') || bed.name.startsWith('PAIN') || bed.name.startsWith('ONC') || bed.name.startsWith('ICU'))) {
      const parts = bed.name.split('-');
      if (parts.length >= 1) {
        return parts[0]; // e.g., "ED" from "ED-R1-B1"
      }
    }
    
    // If we have a bedId that looks like a department code, use it
    if (bed.bedId && (bed.bedId.startsWith('ED') || bed.bedId.startsWith('PAIN') || bed.bedId.startsWith('ONC') || bed.bedId.startsWith('ICU'))) {
      const parts = bed.bedId.split('-');
      if (parts.length >= 1) {
        return parts[0];
      }
    }
    
    // Last resort: try to extract any meaningful prefix from the name
    if (bed.name && bed.name.length > 0) {
      // Look for patterns like "ED-R1-B1", "PAIN-R1-B1", etc.
      const match = bed.name.match(/^([A-Z]+)-?/);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return 'Unknown Room';
  };

  // Helper function to extract bed name/identifier
  const getBedName = (bed) => {
    if (bed.name) return bed.name;
    if (bed.bedId) return bed.bedId;
    if (bed.id) return bed.id;
    return 'Unknown Bed';
  };

  // Create a combined view of all beds and rooms
  const allBeds = [
    ...(availableBeds || []).map(bed => {
      console.log('Processing available bed:', bed);
      const roomName = getRoomName(bed);
      console.log('Extracted roomName for bed:', roomName);
      const processedBed = {
        ...bed,
        type: 'bed',
        status: 'available',
        roomName: roomName,
        bedName: getBedName(bed),
        departmentName: bed.departmentName || 'Unknown'
      };
      console.log('Processed bed:', processedBed);
      return processedBed;
    }),
    ...(activeBedAssignments || []).map(assignment => {
      const processedAssignment = {
        ...assignment,
        type: 'bed',
        status: 'occupied',
        roomName: getRoomName(assignment),
        bedName: getBedName(assignment),
        departmentName: assignment.departmentName || 'Unknown',
        patientName: assignment.patientName || 'Unknown Patient',
        occupiedSince: assignment.assignedAt || assignment.createdAt
      };
      return processedAssignment;
    })
  ];

  // Debug logging
  console.log('BedAssignmentGrid - availableBeds:', availableBeds);
  console.log('BedAssignmentGrid - allBeds:', allBeds);
  console.log('BedAssignmentGrid - Sample bed roomName:', allBeds[0]?.roomName);
  console.log('BedAssignmentGrid - Sample bed departmentName:', allBeds[0]?.departmentName);

  const allRooms = [
    ...(availableRooms || []).map(room => ({
      ...room,
      type: 'room',
      status: 'available',
      roomName: room.name || room.roomId || 'Unknown Room',
      departmentName: room.departmentName || 'Unknown'
    })),
    ...(activeRoomAssignments || []).map(assignment => ({
      ...assignment,
      type: 'room',
      status: 'occupied',
      roomName: assignment.roomName || assignment.roomId || 'Unknown Room',
      departmentName: assignment.departmentName || 'Unknown',
      patientName: assignment.patientName || 'Unknown Patient',
      occupiedSince: assignment.assignedAt || assignment.createdAt
    }))
  ];

  // Group by department
  const departmentGroups = {};
  
  // Group beds by department
  allBeds.forEach(bed => {
    const deptName = bed.departmentName || 'Unknown Department';
    if (!departmentGroups[deptName]) {
      departmentGroups[deptName] = { beds: [], rooms: [] };
    }
    departmentGroups[deptName].beds.push(bed);
  });

  // Group rooms by department
  allRooms.forEach(room => {
    const deptName = room.departmentName || 'Unknown Department';
    if (!departmentGroups[deptName]) {
      departmentGroups[deptName] = { beds: [], rooms: [] };
    }
    departmentGroups[deptName].rooms.push(room);
  });

  const filteredDepartments = selectedDepartment === 'all' 
    ? Object.keys(departmentGroups)
    : Object.keys(departmentGroups).filter(dept => dept === selectedDepartment);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Typography>Loading bed assignments...</Typography>
      </Box>
    );
  }

  // Show empty state if no data
  if ((!availableBeds || availableBeds.length === 0) && 
      (!availableRooms || availableRooms.length === 0) && 
      (!activeBedAssignments || activeBedAssignments.length === 0) && 
      (!activeRoomAssignments || activeRoomAssignments.length === 0)) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Typography>No bed assignments found.</Typography>
      </Box>
    );
  }

  // Show empty state if no departments to display
  if (!filteredDepartments || filteredDepartments.length === 0) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Typography>No departments found to display.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Department Sections */}
      {(filteredDepartments || []).map((departmentName) => {
        const deptData = departmentGroups[departmentName];
        if (!deptData) return null;
        
        const totalBeds = deptData.beds?.length || 0;
        const totalRooms = deptData.rooms?.length || 0;
        
        return (
          <Box key={departmentName} sx={{ mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              pb: 1,
              borderBottom: '2px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="h6" sx={{ 
                color: '#2d3748', 
                fontWeight: 600,
                fontSize: '1.1rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                {departmentName}
                <Chip
                  size="small"
                  label={`${totalBeds + totalRooms} total`}
                  sx={{ ml: 1, height: '20px' }}
                />
              </Typography>
            </Box>
            
            {/* Beds organized by room - each row is a room with its beds */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                Beds by Room ({totalBeds})
              </Typography>
              
              {/* Group beds by room */}
              {(() => {
                const bedsByRoom = {};
                
                // Group beds by room
                (deptData.beds || []).forEach(bed => {
                  const roomKey = bed.roomName || 'Unknown Room';
                  if (!bedsByRoom[roomKey]) {
                    bedsByRoom[roomKey] = [];
                  }
                  bedsByRoom[roomKey].push(bed);
                });
                
                return Object.entries(bedsByRoom).map(([roomName, beds]) => (
                  <Box 
                    key={roomName} 
                    sx={{ 
                      mb: 2,
                      display: 'flex',
                      alignItems: 'stretch',
                      gap: 1.5
                    }}
                  >
                    {/* Room Name Box - First column, styled distinctly */}
                    <Box sx={{
                      minWidth: '120px',
                      maxWidth: '120px',
                      p: 1.5,
                      border: '2px solid',
                      borderColor: 'primary.main',
                      borderRadius: '8px',
                      backgroundColor: 'primary.light',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center'
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 700,
                        color: 'primary.contrastText',
                        fontSize: '0.85rem',
                        lineHeight: 1.2
                      }}>
                        {roomName}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        color: 'primary.contrastText',
                        fontSize: '0.7rem',
                        opacity: 0.9,
                        mt: 0.5
                      }}>
                        {beds.length} bed{beds.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    
                    {/* Beds in this room - displayed horizontally */}
                    <Box sx={{ display: 'flex', gap: 1.5, flex: 1 }}>
                      {beds.map((bed) => (
                        <BedCard 
                          key={`bed-${bed.bedId || bed.id}`}
                          status={bed.status}
                          sx={{ flex: '0 0 auto' }}
                        >
                          <CardContent>
                            {/* Bed Name - Main identifier */}
                            <Typography variant="subtitle2" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              mb: 1
                            }}>
                              {bed.bedName}
                            </Typography>
                            
                            {bed.status === 'occupied' ? (
                              <>
                                {/* Patient Name */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <PersonIcon fontSize="small" sx={{ fontSize: '0.8rem' }} />
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                    {bed.patientName}
                                  </Typography>
                                </Box>
                                
                                {/* Duration */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                  <AccessTimeIcon fontSize="small" sx={{ fontSize: '0.8rem' }} />
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                    {calculateDaysOccupied(bed.occupiedSince)} days
                                  </Typography>
                                </Box>
                                
                                {/* Status and Actions */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Chip
                                    size="small"
                                    label="Occupied"
                                    sx={{
                                      bgcolor: '#fff5f5',
                                      color: '#c53030',
                                      borderColor: '#feb2b2',
                                      height: '18px',
                                      fontSize: '0.65rem'
                                    }}
                                  />
                                  <Box sx={{ display: 'flex', gap: 0.25 }}>
                                    <IconButton
                                      size="small"
                                      sx={{ p: 0.25 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onView && onView(bed);
                                      }}
                                    >
                                      <ViewIcon sx={{ fontSize: '0.8rem' }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      sx={{ p: 0.25 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDischarge && onDischarge(bed);
                                      }}
                                    >
                                      <DischargeIcon sx={{ fontSize: '0.8rem' }} />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Chip
                                  size="small"
                                  label="Available"
                                  sx={{
                                    bgcolor: '#f0fff4',
                                    color: '#2f855a',
                                    borderColor: '#9ae6b4',
                                    height: '18px',
                                    fontSize: '0.65rem'
                                  }}
                                />
                              </Box>
                            )}
                          </CardContent>
                        </BedCard>
                      ))}
                    </Box>
                  </Box>
                ));
              })()}
            </Box>
            
            {/* Standalone Rooms */}
            {totalRooms > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                  Standalone Rooms ({totalRooms})
                </Typography>
                <Grid container spacing={2}>
                  {(deptData.rooms || []).map((room) => {
                    if (!room) return null;
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={`room-${room.roomId || room.id}`}>
                        <BedCard status={room.status}>
                          <CardContent>
                            {/* Room Name */}
                            <Typography variant="subtitle2" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              mb: 1
                            }}>
                              {room.roomName}
                            </Typography>
                            
                            {room.status === 'occupied' ? (
                              <>
                                {/* Patient Name */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <PersonIcon fontSize="small" sx={{ fontSize: '0.8rem' }} />
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                                    {room.patientName}
                                  </Typography>
                                </Box>
                                
                                {/* Duration */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                  <AccessTimeIcon fontSize="small" sx={{ fontSize: '0.8rem' }} />
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                    {calculateDaysOccupied(room.occupiedSince)} days
                                  </Typography>
                                </Box>
                                
                                {/* Status and Actions */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Chip
                                    size="small"
                                    label="Occupied"
                                    sx={{
                                      bgcolor: '#fff5f5',
                                      color: '#c53030',
                                      borderColor: '#feb2b2',
                                      height: '18px',
                                      fontSize: '0.65rem'
                                    }}
                                  />
                                  <Box sx={{ display: 'flex', gap: 0.25 }}>
                                    <IconButton
                                      size="small"
                                      sx={{ p: 0.25 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onView && onView(room);
                                      }}
                                    >
                                      <ViewIcon sx={{ fontSize: '0.8rem' }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      sx={{ p: 0.25 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDischarge && onDischarge(room);
                                      }}
                                    >
                                      <DischargeIcon sx={{ fontSize: '0.8rem' }} />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Chip
                                  size="small"
                                  label="Available"
                                  sx={{
                                    bgcolor: '#f0fff4',
                                    color: '#2f855a',
                                    borderColor: '#9ae6b4',
                                    height: '18px',
                                    fontSize: '0.65rem'
                                  }}
                                />
                              </Box>
                            )}
                          </CardContent>
                        </BedCard>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </Box>
        );
      })}
      
      {/* Show message if no data */}
      {(filteredDepartments || []).length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No bed or room data available for the selected department.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BedAssignmentGrid;

